import React from 'react'
import moment from 'moment-timezone'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import filesize from 'filesize'
import config from '../../../config'
const { contentTimezone } = config as any

import {
  IconMenu,
  IconButton,
  DropDownMenu,
  FlatButton,
  MenuItem
} from 'material-ui'

import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
  TableFooter
} from 'material-ui/Table'

import ArrowLeft from 'material-ui/svg-icons/hardware/keyboard-arrow-left'
import ArrowRight from 'material-ui/svg-icons/hardware/keyboard-arrow-right'
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert'

const ListTable = (props) => {
  const columns = props.columns.slice()

  if (props.query.with_deleted) {
    columns.push({ title: 'Deleted', prop: 'deleted_at', format: 'fromNow' })
  }

  const getSortArrow = (col) => {
    if (props.query.order[0].column !== col.prop) {
      return ''
    }

    return props.query.order[0].direction === 'asc'
      ? <span>&uarr;</span>
      : <span>&darr;</span>
  }

  const getColumnValue = (col, item) => {
    let val = get(item, col.prop)

    if (col.references) {
      const path = [col.references, col.prop]
      const map = get(props.relationships, path, {})
      if (map[val]) {
        val = map[val][col.referenceProp]
      }
    }

    if (col.format === 'bool') {
      return val ? 'Yes' : 'No'
    }

    if (col.format === 'percentage') {
      return val ? `${val}%` : ''
    }

    if (col.format === 'html') {
      const element = document.createElement('div')
      element.innerHTML = val
      return element.innerText
    }

    if (col.format === 'fromNow') {
      return val ? moment(val).fromNow() : ''
    }

    if (col.format === 'date') {
      return val ? moment(val).format('MMM D, YYYY') : ''
    }

    if (col.format === 'dateWithDay') {
      return val ? moment(val).format('ddd MMM D, YYYY') : ''
    }

    if (col.format === 'dateWithTime') {
      return val ? moment(val).tz(contentTimezone).format('MMM D, YYYY @ h:mm a zz') : ''
    }

    if (col.format === 'currency') {
      return val ? '$' + val.toFixed(2) : ''
    }

    if (col.format === 'filesize') {
      return filesize(val)
    }

    if (col.format === 'array' && isArray(val)) {
      return val.join(', ')
    }

    if (col.format === 'json') {
      return JSON.stringify(val)
    }

    if (typeof col.format === 'function') {
      return col.format(val, item)
    }

    return val
  }

  const getColumnStyle = (col) => {
    const style = {
      height: 30,
      paddingLeft: 20,
      paddingRight: 20,
      whiteSpace: 'wrap'
    }

    if (col.prop === 'id' || col.prop === 'position') {
      return {
        ...style,
        width: 45
      }
    }

    if (col.format === 'bool' || col.format === 'number' || col.format === 'percentage') {
      return {
        ...style,
        width: 45,
        textAlign: 'center'
      }
    }

    if (col.format === 'date') {
      return {
        ...style,
        width: 100
      }
    }

    if (col.format === 'dateWithTime') {
      return {
        ...style,
        width: 170
      }
    }

    if (col.format === 'currency') {
      return {
        ...style,
        width: 100,
        textAlign: 'right'
      }
    }

    if (col.format === 'small') {
      return {
        ...style,
        width: 55
      }
    }

    return style
  }

  const menuColumnStyle = {
    padding: 0,
    width: 40
  }

  const offset = parseInt(props.query.offset, 10)
  const limit = parseInt(props.query.limit, 10)
  const collection = props.collection
  const numItems = parseInt(collection.length, 10)
  const { paginationTotal } = props
  const start = offset + 1
  const end = start + numItems - 1
  const showPrevPage = offset > 0
  const showNextPage = props.paginationTotal > offset + limit
  const paginationText = `${start}-${end} of ${paginationTotal}`
  if (props.sortFunction) {
    collection.sort(props.sortFunction)
  }

  return (
    <Table>
      <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
        <TableRow>
          {columns.map(col => (
            <TableHeaderColumn key={col.prop} style={getColumnStyle(col)}>
              {(col.hasOwnProperty('sortable') && !col.sortable) || col.references // eslint-disable-line
                ? (col.title || col.name)
                : <a href='#' onClick={props.handleSortColumn.bind(null, col)}>{(col.title || col.name)} {getSortArrow(col)}</a>}
            </TableHeaderColumn>
          ))}
          {props.hideEdit ? '' : <TableHeaderColumn style={menuColumnStyle} />}
        </TableRow>
      </TableHeader>
      <TableBody displayRowCheckbox={false}>
        {collection.map((item, i) => (
          <TableRow selectable={false} key={i}>
            {columns.map(col => (
              <TableRowColumn key={col.prop} style={getColumnStyle(col)}>{getColumnValue(col, item)}</TableRowColumn>
            ))}
            {props.hideEdit ? '' : <TableRowColumn style={menuColumnStyle}>
              <IconMenu
                iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
                onItemClick={props.handleMenuItemTouchTap}
              >{props.buildMenuItems(item)}
              </IconMenu>
            </TableRowColumn>}
          </TableRow>
        ))}
      </TableBody>
      <TableFooter adjustForCheckbox={false} style={{ display: props.hidePagination ? 'none' : '' }}>
        <TableRow>
          <TableRowColumn style={{ textAlign: 'right', paddingRight: 0 }}>
            Per page:

            <DropDownMenu style={{ verticalAlign: 'bottom' }} value={parseInt(props.query.limit, 10)} onChange={props.handleLimitChange}>
              <MenuItem value={10} primaryText='10' />
              <MenuItem value={20} primaryText='20' />
              <MenuItem value={50} primaryText='50' />
              <MenuItem value={100} primaryText='100' />
              <MenuItem value={250} primaryText='250' />
            </DropDownMenu>

            {paginationText}
            <FlatButton onClick={props.handlePrevPage} icon={<ArrowLeft />} disabled={!showPrevPage} />
            <FlatButton onClick={props.handleNextPage} icon={<ArrowRight />} disabled={!showNextPage} />
          </TableRowColumn>
        </TableRow>
      </TableFooter>
    </Table>
  )
}

// @ts-ignore
ListTable.propTypes = {
  query: React.PropTypes.shape({
  }).isRequired,
  columns: React.PropTypes.arrayOf(React.PropTypes.shape({
    title: React.PropTypes.string.isRequired,
    prop: React.PropTypes.string.isRequired
  })).isRequired,
  collection: React.PropTypes.array.isRequired,
  relationships: React.PropTypes.object.isRequired,
  handleSortColumn: React.PropTypes.func.isRequired,
  handleMenuItemTouchTap: React.PropTypes.func.isRequired,
  handleLimitChange: React.PropTypes.func.isRequired,
  handlePrevPage: React.PropTypes.func.isRequired,
  handleNextPage: React.PropTypes.func.isRequired,
  buildMenuItems: React.PropTypes.func.isRequired,
  paginationTotal: React.PropTypes.number
}

// @ts-ignore
ListTable.defaultProps = {
  paginationTotal: 0
}

export default ListTable
